import Avatar from './Avatar';
import SupportChatWidget from './SupportChatWidget';
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  @keyframes unveilPop {
    0% { opacity: 0; transform: scale(0.8) translateY(10px); }
    70% { transform: scale(1.05) translateY(-2px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  .overview-btn-primary {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .overview-btn-primary:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.35) !important;
  }
  .overview-btn-secondary:hover {
    background-color: rgba(0,128,129,0.1) !important;
    border-color: rgba(0,128,129,0.4) !important;
  }
  .overview-cat-btn:hover {
    border-color: #008081 !important;
    background-color: rgba(0,128,129,0.08) !important;
  }
  .unveil-btn-appear {
    animation: unveilPop 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
  }
`;

export default function OverviewView({ userData, partner, userId, user, setUserData, activeVeylId, theme, t }) {
  const { t: translate } = useLanguage();
  const [showGoalReset, setShowGoalReset] = useState(false);
  const [showUnveilConfirm, setShowUnveilConfirm] = useState(false);
  const [newGoalDescription, setNewGoalDescription] = useState(userData?.goalDescription || '');
  const [newGoalCategory, setNewGoalCategory] = useState(userData?.goalCategory || '');
  const [loading, setLoading] = useState(false);
  const [unveiling, setUnveiling] = useState(false);
  const [liveVeylData, setLiveVeylData] = useState(null);

  useEffect(() => {
    if (!activeVeylId) return;
    const unsubscribe = onSnapshot(doc(db, 'veyls', activeVeylId), (snap) => {
      if (snap.exists()) setLiveVeylData(snap.data());
    });
    return () => unsubscribe();
  }, [activeVeylId]);

  const dark = theme === 'dark';

  const categories = [
    { key: 'fitnessHealth',           value: 'Fitness & Health' },
    { key: 'learningSkills',          value: 'Learning & Skills' },
    { key: 'businessCareer',          value: 'Business & Career' },
    { key: 'creativeProjects',        value: 'Creative Projects' },
    { key: 'relationshipsConnection', value: 'Relationships & Connection' },
    { key: 'homeLifeOrganization',    value: 'Home & Life Organization' },
    { key: 'moneyFinancialHabits',    value: 'Money & Financial Habits' },
    { key: 'stayingConnected',        value: 'Staying Connected' },
    { key: 'independentLiving',       value: 'Independent Living' },
    { key: 'techConfidence',          value: 'Tech Confidence' },
  ];

  const translateCategory = (categoryValue) => {
    const match = categories.find(c => c.value === categoryValue);
    return match ? translate(match.key) : categoryValue;
  };

  const isUnveiled   = liveVeylData?.unveiled     ?? userData?.activeVeyl?.unveiled     ?? userData?.unveiled;
  const goalAchieved = liveVeylData?.goalAchieved ?? userData?.activeVeyl?.goalAchieved ?? userData?.goalAchieved;
  const veylStatus   = liveVeylData?.status       ?? userData?.activeVeyl?.status       ?? userData?.status;
  const unveiledAvatarId = isUnveiled ? 'unveiled' : userData?.avatarId;

  const card = {
    backgroundColor: t.cardBg,
    borderRadius: '1.25rem',
    padding: '1.75rem',
    border: `1px solid ${t.cardBorder}`,
    animation: 'fadeInUp 0.4s ease'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: `1px solid ${t.cardBorder}`,
    borderRadius: '0.75rem',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    backgroundColor: dark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
    color: t.text,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box'
  };

  const handleGoalReset = async () => {
    if (!newGoalDescription.trim()) { alert(translate('goalDescription')); return; }
    setLoading(true);
    const functions = getFunctions();
    const sendNotification = httpsCallable(functions, 'sendNotification');
    try {
      if (activeVeylId) {
        await updateDoc(doc(db, 'veyls', activeVeylId), {
          goalDescription: newGoalDescription,
          goalCategory: newGoalCategory,
          goalLastUpdated: serverTimestamp()
        });
      }
      if (userData.partnerId) {
        await sendNotification({
          recipientId: userData.partnerId,
          type: 'goal_update',
          message: `${userData.name} has adjusted their goal. Check it out!`,
        }).catch(err => console.error('Goal update notification error:', err));
      }
      setUserData(prev => ({
        ...prev,
        goalDescription: newGoalDescription,
        goalCategory: newGoalCategory,
        activeVeyl: { ...prev.activeVeyl, goalDescription: newGoalDescription, goalCategory: newGoalCategory }
      }));
      setShowGoalReset(false);
      alert(translate('goalUpdated'));
    } catch (err) {
      alert(translate('error') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnveil = async () => {
    setUnveiling(true);
    const functions = getFunctions();
    const sendNotification = httpsCallable(functions, 'sendNotification');
    try {
      if (activeVeylId) {
        await updateDoc(doc(db, 'veyls', activeVeylId), {
          unveiled: true,
          unveiledAt: serverTimestamp(),
          status: 'complete'
        });
      }
      const newGoalsAchieved = (userData?.goalsAchieved || 0) + 1;
      await updateDoc(doc(db, 'users', userId), { goalsAchieved: newGoalsAchieved });
      if (userData.partnerId) {
        await sendNotification({
          recipientId: userData.partnerId,
          type: 'unveil',
          message: `${userData.name} has achieved their goal and unveiled! 🎉`,
        }).catch(err => console.error('Unveil notification error:', err));
      }
      setUserData(prev => ({
        ...prev,
        goalsAchieved: newGoalsAchieved,
        unveiled: true,
        activeVeyl: { ...prev.activeVeyl, unveiled: true, status: 'complete' }
      }));
      setShowUnveilConfirm(false);
    } catch (err) {
      alert(translate('error') + ': ' + err.message);
    } finally {
      setUnveiling(false);
    }
  };

  if (showGoalReset) {
    return (
      <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
        <style>{styleTag}</style>
        <div style={{ ...card }}>
          <button
            onClick={() => setShowGoalReset(false)}
            style={{ marginBottom: '1.25rem', color: t.subtext, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
          >
            ← {translate('cancel')}
          </button>

          <h2 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '0.4rem', color: t.text }}>
            {translate('adjustYourGoal')}
          </h2>
          <p style={{ color: t.subtext, marginBottom: '2rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
            {translate('adjustGoalDesc')}
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.75rem', color: t.subtext, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {translate('goalCategory2')}
            </label>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {categories.map(cat => (
                <button
                  key={cat.value}
                  className="overview-cat-btn"
                  onClick={() => setNewGoalCategory(cat.value)}
                  style={{
                    padding: '0.875rem 1rem',
                    border: newGoalCategory === cat.value ? '2px solid #008081' : `2px solid ${t.cardBorder}`,
                    backgroundColor: newGoalCategory === cat.value
                      ? 'rgba(0,128,129,0.12)'
                      : (dark ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                    borderRadius: '0.75rem', textAlign: 'left', cursor: 'pointer',
                    fontWeight: '600', color: newGoalCategory === cat.value ? '#008081' : t.text,
                    fontSize: '0.9rem', transition: 'all 0.15s'
                  }}
                >
                  {translate(cat.key)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: t.subtext, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {translate('newGoalDescription')}
            </label>
            <textarea
              value={newGoalDescription}
              onChange={(e) => setNewGoalDescription(e.target.value)}
              style={{ ...inputStyle, height: '8rem', resize: 'vertical' }}
              placeholder={translate('newGoalPlaceholder')}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowGoalReset(false)}
              style={{ flex: 1, border: `1px solid ${t.cardBorder}`, backgroundColor: 'transparent', color: t.subtext, padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              {translate('cancel')}
            </button>
            <button
              className="overview-btn-primary"
              onClick={handleGoalReset}
              disabled={loading || !newGoalDescription.trim()}
              style={{ flex: 2, background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '700', border: 'none', cursor: (loading || !newGoalDescription.trim()) ? 'not-allowed' : 'pointer', opacity: (loading || !newGoalDescription.trim()) ? 0.5 : 1, fontSize: '0.9rem', boxShadow: '0 4px 15px rgba(0,128,129,0.3)' }}
            >
              {loading ? translate('updating') : translate('updateGoal')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showUnveilConfirm) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <style>{styleTag}</style>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '1rem', animation: 'pulse 2s infinite' }}>🎉</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '0.75rem', color: t.text }}>
            {translate('readyToUnveil')}
          </h2>
          <p style={{ color: t.subtext, fontSize: '0.875rem', lineHeight: '1.7', marginBottom: '1.75rem' }}>
            {translate('unveilDesc')}
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.75rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(192,132,252,0.1)',
                border: '2px solid rgba(192,132,252,0.3)',
                borderRadius: '50%',
                display: 'inline-block',
                marginBottom: '0.75rem'
              }}>
                <Avatar username={userData?.name} avatarId="unveiled" size={72} />
              </div>
              <div style={{ fontSize: '0.75rem', color: t.subtext, fontWeight: '500' }}>
                {translate('yourNewAvatar')}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowUnveilConfirm(false)}
              style={{ flex: 1, border: `1px solid ${t.cardBorder}`, backgroundColor: 'transparent', color: t.subtext, padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
            >
              {translate('notYet')}
            </button>
            <button
              className="overview-btn-primary"
              onClick={handleUnveil}
              disabled={unveiling}
              style={{ flex: 2, background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '700', border: 'none', cursor: unveiling ? 'not-allowed' : 'pointer', opacity: unveiling ? 0.6 : 1, boxShadow: '0 4px 15px rgba(75,44,94,0.4)' }}
            >
              {unveiling ? translate('unveiling') : '✨ ' + translate('unveil')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styleTag}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

        {/* Your Goal Card */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: '700', color: t.subtext, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
              {translate('yourGoal')}
            </h3>
            {veylStatus === 'complete' && (
              <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#16a34a', backgroundColor: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.25)', padding: '0.2rem 0.6rem', borderRadius: '9999px' }}>
                ✓ {translate('goalComplete')}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '1rem', backgroundColor: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: '0.875rem', border: `1px solid ${t.cardBorder}` }}>
            <Avatar username={userData?.name} avatarId={unveiledAvatarId} size={48} />
            <div>
              <div style={{ fontWeight: '700', color: t.text, fontSize: '0.95rem' }}>
                {userData?.name}
                {isUnveiled && (
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#c084fc', fontWeight: '700', backgroundColor: 'rgba(192,132,252,0.1)', padding: '0.15rem 0.4rem', borderRadius: '9999px' }}>
                    {translate('unveiled')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: t.subtext }}>{translate('you')}</div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.75rem', color: t.subtext, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
              {translate('goalCategory2')}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(0,128,129,0.1)', border: '1px solid rgba(0,128,129,0.2)', borderRadius: '9999px', padding: '0.25rem 0.75rem', fontSize: '0.85rem', fontWeight: '600', color: '#008081' }}>
              {translateCategory(userData?.goalCategory)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.75rem', color: t.subtext, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
              {translate('goalDescription')}
            </div>
            <p style={{ color: t.text, fontSize: '0.95rem', lineHeight: 1.7, margin: 0, padding: '0.875rem', backgroundColor: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: '0.75rem', border: `1px solid ${t.cardBorder}` }}>
              {userData?.goalDescription}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: 'auto' }}>
            {goalAchieved && !isUnveiled && veylStatus !== 'complete' && (
              <button
                className="overview-btn-primary unveil-btn-appear"
                onClick={() => setShowUnveilConfirm(true)}
                style={{ width: '100%', padding: '0.875rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem', boxShadow: '0 4px 15px rgba(75,44,94,0.35)' }}
              >
                ✨ {translate('unveilButton')}
              </button>
            )}
            {veylStatus !== 'complete' && (
              <button
                className="overview-btn-secondary"
                onClick={() => setShowGoalReset(true)}
                style={{ width: '100%', padding: '0.875rem', backgroundColor: 'transparent', color: '#008081', border: '1px solid rgba(0,128,129,0.35)', borderRadius: '0.75rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s' }}
              >
                ✏️ {translate('adjustGoal')}
              </button>
            )}
          </div>
        </div>

        {/* Partner Card */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: '700', color: t.subtext, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
              {translate('yourPartner')}
            </h3>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: partner ? '#16a34a' : '#E05C5C', display: 'inline-block', animation: 'pulse 2s infinite' }} />
          </div>

          {partner ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '1rem', backgroundColor: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: '0.875rem', border: `1px solid ${t.cardBorder}` }}>
                <Avatar username={partner.name} avatarId={partner.unveiled ? 'unveiled' : partner.avatarId} size={48} />
                <div>
                  <div style={{ fontWeight: '700', color: t.text, fontSize: '0.95rem' }}>
                    {partner.name}
                    {partner.unveiled && (
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#c084fc', fontWeight: '700', backgroundColor: 'rgba(192,132,252,0.1)', padding: '0.15rem 0.4rem', borderRadius: '9999px' }}>
                        {translate('unveiled')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: t.subtext }}>{translate('yourPartner')}</div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '0.75rem', color: t.subtext, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                  {translate('goalDescription')}
                </div>
                <p style={{ color: t.text, fontSize: '0.95rem', lineHeight: 1.7, margin: 0, padding: '0.875rem', backgroundColor: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: '0.75rem', border: `1px solid ${t.cardBorder}` }}>
                  {partner.goalDescription || <span style={{ color: t.subtext, fontStyle: 'italic' }}>{translate('loadingPartner')}</span>}
                </p>
              </div>

              <div>
                <div style={{ fontSize: '0.75rem', color: t.subtext, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
                  {translate('timezone')}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: dark ? 'rgba(255,255,255,0.04)' : '#f9fafb', border: `1px solid ${t.cardBorder}`, borderRadius: '9999px', padding: '0.25rem 0.75rem', fontSize: '0.85rem', color: t.text, fontWeight: '500' }}>
                  🕐 {partner.timezone}
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', gap: '0.75rem' }}>
              <div style={{ fontSize: '2.5rem' }}>👤</div>
              <p style={{ color: t.subtext, fontSize: '0.875rem', margin: 0 }}>{translate('loadingPartner')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Support Chat — moved here from the header to avoid mobile popover overflow */}
      <div style={{ marginTop: '1.5rem' }}>
        <SupportChatWidget user={user} theme={theme} t={t} translate={translate} />
      </div>
    </>
  );
}